import { useState, useEffect } from 'react';
import {
  Smartphone,
  Plus,
  RefreshCw,
  Wifi,
  WifiOff,
  Trash2,
  Loader,
  CheckCircle,
  AlertCircle,
  Radio
} from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

export default function ReaderManagement({ companyId }) {
  const { colors } = useTheme();
  const { showError, showSuccess, showConfirm } = useModal();
  const [loading, setLoading] = useState(true);
  const [readers, setReaders] = useState([]);
  const [terminalEnabled, setTerminalEnabled] = useState(false);
  const [showAddReader, setShowAddReader] = useState(false);
  const [registrationCode, setRegistrationCode] = useState('');
  const [readerLabel, setReaderLabel] = useState('');
  const [registering, setRegistering] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoveredReaders, setDiscoveredReaders] = useState([]);

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load company terminal settings
      const { data: company } = await supabaseData
        .from('companies')
        .select('terminal_enabled')
        .eq('id', companyId)
        .single();

      setTerminalEnabled(company?.terminal_enabled || false);

      // Load readers
      await loadReaders();
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const loadReaders = async () => {
    try {
      const response = await fetch(`/api/terminal?action=list-readers&companyId=${companyId}`);
      const data = await response.json();

      if (response.ok) {
        setReaders(data.readers || []);
      }
    } catch (err) {
    }
  };

  const handleToggleTerminal = async (enabled) => {
    try {
      const { error } = await supabaseData
        .from('companies')
        .update({ terminal_enabled: enabled })
        .eq('id', companyId);

      if (error) throw error;

      setTerminalEnabled(enabled);
      showSuccess(enabled ? 'Terminal payments enabled' : 'Terminal payments disabled');
    } catch (err) {
      showError('Failed to update terminal setting');
    }
  };

  const handleRegisterReader = async () => {
    if (!registrationCode.trim()) {
      showError('Please enter a registration code');
      return;
    }

    setRegistering(true);
    try {
      const response = await fetch('/api/terminal?action=register-reader', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          registrationCode: registrationCode.trim(),
          label: readerLabel.trim() || 'Reader',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to register reader');
      }

      showSuccess('Reader registered successfully');
      setShowAddReader(false);
      setRegistrationCode('');
      setReaderLabel('');
      await loadReaders();
    } catch (err) {
      showError(err.message);
    } finally {
      setRegistering(false);
    }
  };

  const handleDiscoverReaders = async (simulated = false) => {
    setDiscovering(true);
    setDiscoveredReaders([]);

    try {
      // This would use the Terminal SDK in the browser
      // For now, show simulated readers for testing
      if (simulated) {
        setDiscoveredReaders([
          {
            id: 'tmr_simulated_1',
            label: 'Simulated Reader',
            device_type: 'simulated',
            status: 'online',
            simulated: true,
          },
        ]);
      } else {
        showError('Physical reader discovery requires the Terminal SDK. Use "Add with Code" for physical readers.');
      }
    } catch (err) {
      showError('Failed to discover readers: ' + err.message);
    } finally {
      setDiscovering(false);
    }
  };

  const handleDeleteReader = async (reader) => {
    const confirmed = await showConfirm(
      `Remove "${reader.label || 'this reader'}"?`,
      { confirmStyle: 'danger' }
    );

    if (!confirmed) return;

    try {
      // Delete from our database
      await supabaseData
        .from('terminal_readers')
        .delete()
        .eq('reader_id', reader.id);

      showSuccess('Reader removed');
      await loadReaders();
    } catch (err) {
      showError('Failed to remove reader');
    }
  };

  const getDeviceTypeLabel = (deviceType) => {
    const types = {
      bbpos_wisepos_e: 'BBPOS WisePOS E',
      stripe_m2: 'Stripe Reader M2',
      verifone_P400: 'Verifone P400',
      stripe_s700: 'Stripe Reader S700',
      simulated: 'Simulated Reader',
    };
    return types[deviceType] || deviceType || 'Unknown';
  };

  if (loading) {
    return (
      <div style={{ padding: '1.5rem', textAlign: 'center', color: colors.textMuted }}>
        <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <div style={{ marginTop: '0.5rem' }}>Loading terminal settings...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Enable/Disable Toggle */}
      <div style={{
        background: colors.bgInput,
        padding: '1rem 1.25rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
            Enable Terminal Payments
          </div>
          <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
            Accept in-person card payments with a card reader
          </div>
        </div>
        <button
          onClick={() => handleToggleTerminal(!terminalEnabled)}
          style={{
            width: '52px',
            height: '28px',
            borderRadius: '14px',
            background: terminalEnabled ? '#4ecca3' : colors.bgHover,
            border: 'none',
            cursor: 'pointer',
            position: 'relative',
            transition: 'background 0.2s',
          }}
        >
          <div
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              background: '#fff',
              position: 'absolute',
              top: '3px',
              left: terminalEnabled ? '27px' : '3px',
              transition: 'left 0.2s',
            }}
          />
        </button>
      </div>

      {terminalEnabled && (
        <>
          {/* Fee Info */}
          <div style={{
            background: '#1a3a2a',
            border: '1px solid #4ecca350',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            color: '#4ecca3',
          }}>
            <strong>Better margins with Terminal!</strong> Card-present payments have lower
            Stripe fees (2.7% + 5c vs 2.9% + 30c), so you keep more of each payment.
          </div>

          {/* Readers List */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}>
            <h4 style={{ margin: 0, color: colors.text }}>Card Readers</h4>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={loadReaders}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: colors.bgHover,
                  border: 'none',
                  borderRadius: '6px',
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  fontSize: '0.85rem',
                }}
              >
                <RefreshCw size={14} /> Refresh
              </button>
              <button
                onClick={() => setShowAddReader(true)}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: '#4ecca3',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#000',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                }}
              >
                <Plus size={14} /> Add Reader
              </button>
            </div>
          </div>

          {readers.length === 0 ? (
            <div style={{
              background: colors.bgInput,
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: colors.textMuted,
              border: `1px dashed ${colors.border}`,
            }}>
              <Smartphone size={32} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
              <div>No card readers connected</div>
              <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Add a reader to start accepting in-person payments
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {readers.map((reader) => (
                <div
                  key={reader.id}
                  style={{
                    background: colors.bgInput,
                    padding: '1rem',
                    borderRadius: '8px',
                    border: reader.status === 'online' ? '1px solid #4ecca350' : '1px solid #3a3a3a',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: reader.status === 'online' ? '#4ecca320' : colors.bgHover,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {reader.status === 'online' ? (
                          <Wifi size={20} color="#4ecca3" />
                        ) : (
                          <WifiOff size={20} color="#888" />
                        )}
                      </div>
                      <div>
                        <div style={{ color: colors.text, fontWeight: 'bold' }}>
                          {reader.label || 'Unnamed Reader'}
                        </div>
                        <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                          {getDeviceTypeLabel(reader.device_type)}
                        </div>
                        {reader.serial_number && (
                          <div style={{ color: colors.textMuted, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                            S/N: {reader.serial_number}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: reader.status === 'online' ? '#4ecca320' : colors.bgHover,
                        color: reader.status === 'online' ? '#4ecca3' : colors.textMuted,
                      }}>
                        {reader.status === 'online' ? 'Online' : 'Offline'}
                      </span>
                      <button
                        onClick={() => handleDeleteReader(reader)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: colors.textMuted,
                          cursor: 'pointer',
                          padding: '0.25rem',
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Get a Reader Section */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1.25rem',
            background: colors.bgCard,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
          }}>
            <h4 style={{ margin: '0 0 1rem', color: colors.text }}>
              Get a Card Reader
            </h4>

            {/* Tap to Pay Option */}
            <div style={{
              padding: '1rem',
              background: colors.bgInput,
              borderRadius: '8px',
              marginBottom: '0.75rem',
              border: '1px solid #4ecca350',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #4ecca3, #45b393)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  flexShrink: 0,
                }}>
                  📱
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#4ecca3', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    Tap to Pay - No Hardware Needed!
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    Use your iPhone or Android as a card reader. Customers tap their card or phone to pay.
                    Same low rate: 2.7% + 5¢
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                    Requires iPhone XS+ (iOS 15.4+) or Android 9+ with NFC
                  </div>
                </div>
              </div>
            </div>

            {/* Physical Reader Options */}
            <div style={{
              padding: '1rem',
              background: colors.bgInput,
              borderRadius: '8px',
            }}>
              <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.75rem' }}>
                Physical Card Readers
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: colors.text }}>Stripe Reader M2</div>
                    <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>Bluetooth, portable - perfect for field work</div>
                  </div>
                  <div style={{ color: '#4ecca3', fontWeight: 'bold' }}>$59</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: colors.text }}>BBPOS WisePOS E</div>
                    <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>Countertop with built-in screen</div>
                  </div>
                  <div style={{ color: '#4ecca3', fontWeight: 'bold' }}>$249</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: colors.text }}>Stripe Reader S700</div>
                    <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>Premium with large touchscreen</div>
                  </div>
                  <div style={{ color: '#4ecca3', fontWeight: 'bold' }}>$349</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <a
                  href="https://stripe.com/terminal#pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#635bff',
                    color: '#fff',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                  }}
                >
                  Buy from Stripe
                </a>
                <a
                  href="https://www.amazon.com/s?k=stripe+m2+reader"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '0.5rem 1rem',
                    background: colors.bgHover,
                    color: colors.text,
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                  }}
                >
                  Buy on Amazon
                </a>
              </div>
            </div>
          </div>

          {/* Test Mode Notice */}
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: colors.bgInput,
            borderRadius: '8px',
            fontSize: '0.85rem',
            color: colors.textMuted,
          }}>
            <div style={{ fontWeight: 'bold', color: '#f39c12', marginBottom: '0.5rem' }}>
              Testing Mode
            </div>
            <div>
              Use the simulated reader for testing without hardware:
              <button
                onClick={() => handleDiscoverReaders(true)}
                disabled={discovering}
                style={{
                  marginLeft: '0.5rem',
                  padding: '0.35rem 0.75rem',
                  background: colors.bgHover,
                  border: 'none',
                  borderRadius: '4px',
                  color: colors.text,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                {discovering ? 'Discovering...' : 'Discover Simulated Reader'}
              </button>
            </div>
            {discoveredReaders.length > 0 && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: colors.bgCard, borderRadius: '6px' }}>
                {discoveredReaders.map((dr) => (
                  <div key={dr.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      <Radio size={14} style={{ marginRight: '0.5rem' }} />
                      {dr.label}
                    </span>
                    <span style={{ color: '#4ecca3', fontSize: '0.75rem' }}>Ready to connect</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Reader Modal */}
      {showAddReader && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 4000,
          padding: '1rem',
        }}>
          <div style={{
            background: colors.bgInput,
            borderRadius: '12px',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '100%',
          }}>
            <h3 style={{ margin: '0 0 1rem', color: colors.text }}>Add Card Reader</h3>

            <p style={{ color: colors.textMuted, fontSize: '0.9rem', marginBottom: '1rem' }}>
              Enter the registration code from your Stripe Terminal reader. You can find this
              on the reader's screen or in your Stripe Dashboard.
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                Registration Code
              </label>
              <input
                type="text"
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value.toUpperCase())}
                placeholder="e.g., pear-apple-lemon"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  color: colors.text,
                  fontSize: '1rem',
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                Reader Label (optional)
              </label>
              <input
                type="text"
                value={readerLabel}
                onChange={(e) => setReaderLabel(e.target.value)}
                placeholder="e.g., Van 1 Reader"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  color: colors.text,
                  fontSize: '1rem',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleRegisterReader}
                disabled={registering}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: registering ? colors.bgHover : '#4ecca3',
                  border: 'none',
                  borderRadius: '6px',
                  color: registering ? colors.textMuted : '#000',
                  fontWeight: 'bold',
                  cursor: registering ? 'not-allowed' : 'pointer',
                }}
              >
                {registering ? 'Registering...' : 'Add Reader'}
              </button>
              <button
                onClick={() => {
                  setShowAddReader(false);
                  setRegistrationCode('');
                  setReaderLabel('');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors.bgHover,
                  border: 'none',
                  borderRadius: '6px',
                  color: colors.text,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
